import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { CourseService } from "src/app/services/api/courses.service";
import { SharedModule } from "src/shared/shared.module";
import { subjectArt } from "src/app/config/subject-art";

interface LandingCategory {
  id: number;
  name: string;
  description?: string;
}

/**
 * Browse by subject, straight off the landing page.
 *
 * This component used to render a hard-coded list of the customer's own courses
 * with prices and a "Buy Now" button that did nothing, and it was not even in
 * the landing page's composition.
 *
 * NOTE ON ART: `course_categories` has no image column and no attachment — the
 * first version of this tile asked for one and every card rendered the broken
 * placeholder. The tiles were typographic after that: a tinted initial block
 * whose hue rotates through the palette.
 *
 * The initial is an English convention and the categories are Thai: the seeded
 * four give "ค" twice and one "แ", which is a vowel, not a letter. The mark is
 * now an icon chosen by keyword from the category name — no image column, no
 * request, nothing to 404 — and a category the map does not recognise still
 * falls back to its initial.
 */
@Component({
  selector: "app-course-categories",
  imports: [CommonModule, SharedModule, RouterModule, TranslateModule],
  templateUrl: "./course-categories.component.html",
  styleUrl: "./course-categories.component.scss",
})
export class CourseCategoriesComponent {
  #courseService = inject(CourseService);

  categories = signal<LandingCategory[]>([]);

  /**
   * Whether the description line is worth showing.
   *
   * The seed gives every category the same sentence, and four identical lines
   * of grey under four different titles do not read as description — they read
   * as a page that failed to load its content. When they are all the same, the
   * title and the icon say everything the row has to say.
   */
  showDescriptions = computed(() => {
    const rows = this.categories();
    const seen = new Set(rows.map((c) => (c.description || "").trim()).filter(Boolean));
    return seen.size > 1;
  });

  private readonly tints = [
    "primary",
    "info",
    "success",
    "warning",
    "danger",
    "primary",
    "info",
    "success",
  ];

  ngOnInit(): void {
    this.#courseService.getCourseCategories({ limit: 8 }).subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res) ? res : (res?.data ?? []);
        this.categories.set(rows.slice(0, 8));
      },
      error: () => this.categories.set([]),
    });
  }

  tint(index: number): string {
    return this.tints[index % this.tints.length];
  }

  initial(category: LandingCategory): string {
    return (category.name || "?").trim().charAt(0);
  }

  /** The icon for a category, or null when nothing matches and the initial stands in. */
  icon(category: LandingCategory): string | null {
    return subjectArt(category.name);
  }
}
